import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, RefreshCw, Ticket, Award, Users, DollarSign, ExternalLink, Code2, Copy, Download, CheckCheck, FileText, Pencil, Trash2, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const EVENTS = [
  { slug: 'trivia-night-2026', label: '2026 Trivia Night' },
  { slug: 'night-in-tuscany', label: 'Night in Tuscany' },
];

export function EventOrdersTab({ api }) {
  const [slug, setSlug] = useState(EVENTS[0].slug);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [deleteOrder, setDeleteOrder] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events-admin/${slug}/orders`);
      setData(data);
    } catch { toast.error('Failed to load event orders'); }
    setLoading(false);
  }, [api, slug]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (orderId) => {
    try {
      await api.post(`/events-admin/orders/${orderId}/mark-paid`);
      toast.success('Check recorded — donor & gift created');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark paid'); }
  };

  const confirmDelete = async () => {
    if (!deleteOrder) return;
    try {
      const { data: res } = await api.delete(`/events-admin/orders/${deleteOrder.id}`);
      toast.success(res.gift_removed
        ? 'Order & linked donor gift removed · donor totals updated'
        : 'Order removed');
      setDeleteOrder(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Purchaser', 'Email', 'Organization', 'Item', 'Type', 'Payment method', 'Quantity', 'Seats', 'Amount', 'Fee cover', 'Status', 'Date', 'Attendees', 'Note'];
    const rows = data.orders.map(o => [
      o.purchaser_name, o.purchaser_email, o.org, o.item_label, o.item_type,
      o.payment_method || 'card', o.quantity, o.seats, o.amount,
      o.fee_amount ? `$${o.fee_amount}` : '', o.payment_status,
      (o.created_at || '').slice(0, 10), (o.attendees || []).join('; '), o.note,
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${slug}-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('CSV downloaded — ready for Neon import');
  };

  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  const { stats, orders } = data;
  const incompleteCount = orders.filter(o => o.payment_status !== 'paid' && o.payment_status !== 'check_pending').length;
  const visibleOrders = showIncomplete ? orders : orders.filter(o => o.payment_status === 'paid' || o.payment_status === 'check_pending');
  const publicUrl = `${window.location.origin}/events/${slug}`;

  return (
    <div className="space-y-4" data-testid="event-orders-tab">
      <div className="flex rounded-xl overflow-hidden border w-fit" data-testid="event-switcher">
        {EVENTS.map(ev => (
          <button key={ev.slug} onClick={() => setSlug(ev.slug)}
                  className={`px-4 py-1.5 text-sm font-semibold ${slug === ev.slug ? 'bg-[#6B3FA0] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
                  data-testid={`event-switch-${ev.slug}`}>
            {ev.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-lg">{data.event}</h2>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-[#6B3FA0] hover:underline flex items-center gap-1"
             data-testid="event-public-link">
            {publicUrl} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="event-export-csv"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button>
          <OfflineTicketDialog api={api} slug={slug} onSaved={load} />
          <OfflineSponsorshipDialog api={api} slug={slug} tiers={data.tiers || []} onSaved={load} />
          <ConfirmedSponsorsDialog api={api} slug={slug} eventName={data.event} />
          <Button size="sm" variant="outline" onClick={load} data-testid="event-orders-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { icon: DollarSign, label: 'Revenue (paid)', value: fmt$(stats.revenue), testid: 'ev-stat-revenue' },
          { icon: Ticket, label: 'Tickets sold', value: stats.tickets_sold, testid: 'ev-stat-tickets' },
          { icon: Award, label: 'Sponsorships', value: stats.sponsorships, testid: 'ev-stat-sponsors' },
          { icon: Users, label: 'Seats reserved', value: stats.seats_reserved, testid: 'ev-stat-seats' },
        ].map(s => (
          <Card key={s.label} data-testid={s.testid}><CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500"><s.icon className="h-3 w-3" /> {s.label}</div>
            <div className="text-xl font-bold tabular-nums mt-0.5">{s.value}</div>
          </CardContent></Card>
        ))}
      </div>
      {stats.checks_pending > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="checks-pending-note">
          {stats.checks_pending} sponsorship{stats.checks_pending === 1 ? '' : 's'} awaiting a check — click &quot;Check received&quot; when it arrives to log the gift.
        </p>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {incompleteCount > 0 && (
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50/60">
              <p className="text-xs text-gray-500">
                {incompleteCount} incomplete checkout{incompleteCount === 1 ? '' : 's'} hidden — these are visitors who opened payment but never finished (no charge made).
              </p>
              <button className="text-xs text-[#6B3FA0] hover:underline shrink-0 ml-2"
                      onClick={() => setShowIncomplete(s => !s)} data-testid="toggle-incomplete">
                {showIncomplete ? 'Hide incomplete' : 'Show incomplete'}
              </button>
            </div>
          )}
          {visibleOrders.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">
              {orders.length === 0 ? 'No orders yet — share the public link above to start selling.' : 'No completed orders yet — paid and check-pending orders will appear here.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[#F7F4EF] text-xs uppercase tracking-wide text-gray-500 text-left">
                  <th className="px-3 py-2">Purchaser</th>
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Seats</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map(o => (
                  <tr key={o.id} className="border-b last:border-b-0" data-testid={`event-order-${o.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{o.purchaser_name}</div>
                      <div className="text-xs text-gray-500">{o.purchaser_email}{o.org ? ` · ${o.org}` : ''}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div>{o.item_label}</div>
                      {(o.attendees || []).length > 0 && (
                        <div className="text-[11px] text-gray-500">Attendees: {o.attendees.join(', ')}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt$(o.amount)}</td>
                    <td className="px-3 py-2 text-right">{o.seats || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Badge className={`border-0 text-[10px] ${o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : o.payment_status === 'check_pending' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                        {o.payment_status === 'check_pending' ? 'check pending' : o.payment_status}
                      </Badge>
                      {o.payment_status === 'check_pending' && (
                        <Button size="sm" variant="outline" className="ml-2 h-6 px-2 text-[11px]"
                                onClick={() => markPaid(o.id)} data-testid={`mark-paid-${o.id}`}>
                          <CheckCheck className="h-3 w-3 mr-1" /> Check received
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <span>{(o.created_at || '').slice(0, 10)}</span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500 hover:text-[#6B3FA0]"
                                onClick={() => setEditOrder(o)}
                                data-testid={`edit-order-${o.id}`} title="Edit order">
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500 hover:text-red-600"
                                onClick={() => setDeleteOrder(o)}
                                data-testid={`del-order-${o.id}`} title="Delete order">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-gray-500">Paid orders automatically create a donor (tagged event-{slug}) and a gift under campaign &quot;{data.event}&quot;.</p>

      <EmbedCard eventName={data.event} publicUrl={publicUrl} />

      {/* Edit order dialog */}
      <EditOrderDialog order={editOrder} api={api}
                       onClose={() => setEditOrder(null)}
                       onSaved={() => { setEditOrder(null); load(); }} />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteOrder} onOpenChange={o => !o && setDeleteOrder(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Delete this order?
            </DialogTitle>
          </DialogHeader>
          {deleteOrder && (
            <div className="text-sm space-y-2 py-1">
              <div className="p-2 bg-gray-50 rounded border">
                <div className="font-medium">{deleteOrder.purchaser_name}</div>
                <div className="text-xs text-gray-500">{deleteOrder.item_label} · {fmt$(deleteOrder.amount)}</div>
              </div>
              {deleteOrder.recorded && deleteOrder.payment_status === 'paid' ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  This order was already recorded as a donor gift. Deleting will also remove that gift and recompute the donor&apos;s totals. The donor record itself is preserved.
                </p>
              ) : (
                <p className="text-xs text-gray-500">This order was never recorded as a gift, so no donor data will change.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrder(null)} data-testid="cancel-delete">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete} data-testid="confirm-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EditOrderDialog({ order, api, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        purchaser_name: order.purchaser_name || '',
        purchaser_email: order.purchaser_email || '',
        org: order.org || '',
        amount: order.amount ?? 0,
        quantity: order.quantity ?? 1,
        seats: order.seats ?? 0,
        item_label: order.item_label || '',
        note: order.note || '',
        attendees: (order.attendees || []).join('\n'),
      });
    } else {
      setForm(null);
    }
  }, [order]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.purchaser_name.trim() || !form.purchaser_email.includes('@')) {
      toast.error('Name and a valid email are required'); return;
    }
    setBusy(true);
    try {
      await api.patch(`/events-admin/orders/${order.id}`, {
        purchaser_name: form.purchaser_name.trim(),
        purchaser_email: form.purchaser_email.trim(),
        org: form.org.trim(),
        amount: Number(form.amount),
        quantity: Number(form.quantity),
        seats: Number(form.seats),
        item_label: form.item_label.trim(),
        note: form.note.trim(),
        attendees: form.attendees.split('\n').map(s => s.trim()).filter(Boolean),
      });
      toast.success(order.payment_status === 'paid' && order.recorded
        ? 'Order updated · linked donor gift & totals also updated'
        : 'Order updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  if (!order || !form) return null;

  return (
    <Dialog open={!!order} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle>Edit order</DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            {order.item_type === 'ticket' ? 'Ticket order' : 'Sponsorship'} · {order.payment_status === 'paid' ? 'Paid — linked gift will update too' : order.payment_status === 'check_pending' ? 'Awaiting check' : 'Not paid'}
          </p>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Purchaser name *</Label>
              <Input value={form.purchaser_name} onChange={e => set('purchaser_name', e.target.value)}
                     data-testid="edit-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.purchaser_email} onChange={e => set('purchaser_email', e.target.value)}
                     data-testid="edit-email" />
            </div>
          </div>
          <div>
            <Label>Organization</Label>
            <Input value={form.org} onChange={e => set('org', e.target.value)} data-testid="edit-org" />
          </div>
          <div>
            <Label>Item label</Label>
            <Input value={form.item_label} onChange={e => set('item_label', e.target.value)} data-testid="edit-item-label" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                     onChange={e => set('amount', e.target.value)} data-testid="edit-amount" />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" min="1" value={form.quantity}
                     onChange={e => set('quantity', e.target.value)} data-testid="edit-quantity" />
            </div>
            <div>
              <Label>Seats</Label>
              <Input type="number" min="0" value={form.seats}
                     onChange={e => set('seats', e.target.value)} data-testid="edit-seats" />
            </div>
          </div>
          <div>
            <Label>Attendee names <span className="text-gray-400 text-[10px] font-normal">(one per line)</span></Label>
            <textarea rows={2} value={form.attendees}
                      onChange={e => set('attendees', e.target.value)}
                      className="w-full mt-1 rounded-xl border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
                      data-testid="edit-attendees" />
          </div>
          <div>
            <Label>Internal note</Label>
            <Input value={form.note} onChange={e => set('note', e.target.value)} data-testid="edit-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-edit-order">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OfflineSponsorshipDialog({ api, slug, tiers, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    mode: 'tier',  // 'tier' | 'custom'
    tier_key: tiers[0]?.key || '',
    custom_amount: '',
    custom_label: '',
    seats: 0,
    purchaser_name: '',
    purchaser_email: '',
    org: '',
    note: '',
    attendees: '',
    mark_paid: true,
  });

  const reset = () => setForm(f => ({
    ...f,
    mode: 'tier',
    custom_amount: '',
    custom_label: '',
    seats: 0,
    purchaser_name: '',
    purchaser_email: '',
    org: '',
    note: '',
    attendees: '',
    mark_paid: true,
  }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.purchaser_name.trim() || !form.purchaser_email.includes('@')) {
      toast.error('Name and a valid email are required'); return;
    }
    if (form.mode === 'custom' && (!form.custom_label.trim() || form.custom_amount === '' || Number(form.custom_amount) < 0)) {
      toast.error('Custom sponsorship needs a label and amount'); return;
    }
    setBusy(true);
    try {
      const body = {
        purchaser_name: form.purchaser_name.trim(),
        purchaser_email: form.purchaser_email.trim(),
        org: form.org.trim(),
        note: form.note.trim(),
        attendees: form.attendees.split('\n').map(s => s.trim()).filter(Boolean),
        mark_paid: !!form.mark_paid,
        seats: Number(form.seats) || 0,
      };
      if (form.mode === 'tier') {
        body.tier_key = form.tier_key;
        if (form.custom_amount !== '') body.custom_amount = Number(form.custom_amount);
      } else {
        body.custom_label = form.custom_label.trim();
        body.custom_amount = Number(form.custom_amount);
      }
      const { data } = await api.post(`/events-admin/${slug}/offline-sponsorship`, body);
      toast.success(form.mark_paid
        ? `Sponsorship recorded · $${data.amount}`
        : `Sponsorship logged · awaiting check · $${data.amount}`);
      setOpen(false); reset(); onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log sponsorship');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="log-offline-sponsorship-btn">
        <Award className="h-3.5 w-3.5 mr-1" /> Log sponsorship by check
      </Button>
      <Dialog open={open} onOpenChange={o => { if (!o) { setOpen(false); reset(); } }}>
        <DialogContent className="max-w-md" data-testid="offline-sponsorship-dialog">
          <DialogHeader>
            <DialogTitle>Log sponsorship paid by check</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Enter a sponsor whose check came in outside Stripe. Choose an existing tier or log a custom amount.
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex rounded-lg overflow-hidden border">
              <button type="button" onClick={() => set('mode', 'tier')}
                      className={`flex-1 px-3 py-1.5 text-sm ${form.mode === 'tier' ? 'bg-[#6B3FA0] text-white' : 'bg-white'}`}
                      data-testid="sponsorship-mode-tier">Tier</button>
              <button type="button" onClick={() => set('mode', 'custom')}
                      className={`flex-1 px-3 py-1.5 text-sm ${form.mode === 'custom' ? 'bg-[#6B3FA0] text-white' : 'bg-white'}`}
                      data-testid="sponsorship-mode-custom">Custom</button>
            </div>
            {form.mode === 'tier' ? (
              <>
                <div>
                  <Label>Sponsorship tier</Label>
                  <select value={form.tier_key} onChange={e => set('tier_key', e.target.value)}
                          className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white"
                          data-testid="sponsorship-tier-select">
                    {tiers.map(t => (
                      <option key={t.key} value={t.key}>{t.name} — {t.title} · ${t.price}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Amount override <span className="text-gray-400 text-[10px] font-normal">(blank = tier price)</span></Label>
                  <Input type="number" step="0.01" min="0" value={form.custom_amount}
                         onChange={e => set('custom_amount', e.target.value)}
                         placeholder="Default"
                         data-testid="sponsorship-amount-override" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Sponsorship label *</Label>
                  <Input value={form.custom_label} onChange={e => set('custom_label', e.target.value)}
                         placeholder="e.g., Table sponsor"
                         data-testid="sponsorship-custom-label" />
                </div>
                <div>
                  <Label>Amount ($) *</Label>
                  <Input type="number" step="0.01" min="0" value={form.custom_amount}
                         onChange={e => set('custom_amount', e.target.value)}
                         data-testid="sponsorship-custom-amount" />
                </div>
                <div>
                  <Label>Seats included</Label>
                  <Input type="number" min="0" value={form.seats}
                         onChange={e => set('seats', e.target.value)}
                         data-testid="sponsorship-seats" />
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Sponsor name *</Label>
                <Input value={form.purchaser_name} onChange={e => set('purchaser_name', e.target.value)}
                       data-testid="sponsorship-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.purchaser_email} onChange={e => set('purchaser_email', e.target.value)}
                       data-testid="sponsorship-email" />
              </div>
            </div>
            <div>
              <Label>Organization</Label>
              <Input value={form.org} onChange={e => set('org', e.target.value)}
                     data-testid="sponsorship-org" />
            </div>
            <div>
              <Label>Attendee names <span className="text-gray-400 text-[10px] font-normal">(optional, one per line)</span></Label>
              <textarea rows={2} value={form.attendees}
                        onChange={e => set('attendees', e.target.value)}
                        className="w-full mt-1 rounded-xl border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
                        data-testid="sponsorship-attendees" />
            </div>
            <div>
              <Label>Internal note <span className="text-gray-400 text-[10px] font-normal">(check #, etc.)</span></Label>
              <Input value={form.note} onChange={e => set('note', e.target.value)}
                     data-testid="sponsorship-note" />
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <input type="checkbox" checked={form.mark_paid}
                     onChange={e => set('mark_paid', e.target.checked)}
                     className="h-4 w-4 mt-0.5" data-testid="sponsorship-mark-paid" />
              <span>
                <span className="font-medium text-emerald-900">Check already in hand — record as paid now</span>
                <span className="block text-[11px] text-emerald-700 mt-0.5">Creates the donor + gift immediately. Uncheck to log as &quot;check pending&quot; and mark paid later.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-offline-sponsorship">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.mark_paid ? 'Log & record' : 'Log as pending')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OfflineTicketDialog({ api, slug, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    quantity: 2, purchaser_name: '', purchaser_email: '', org: '',
    unit_price: '', attendees: '', note: '', mark_paid: true,
  });
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const reset = () => setForm({
    quantity: 2, purchaser_name: '', purchaser_email: '', org: '',
    unit_price: '', attendees: '', note: '', mark_paid: true,
  });

  const save = async () => {
    if (!form.purchaser_name.trim() || !form.purchaser_email.includes('@')) {
      toast.error('Name and a valid email are required'); return;
    }
    setBusy(true);
    try {
      const body = {
        quantity: Number(form.quantity) || 1,
        purchaser_name: form.purchaser_name.trim(),
        purchaser_email: form.purchaser_email.trim(),
        org: form.org.trim(),
        note: form.note.trim(),
        attendees: form.attendees.split('\n').map(s => s.trim()).filter(Boolean),
        mark_paid: !!form.mark_paid,
      };
      if (form.unit_price !== '' && !isNaN(Number(form.unit_price))) {
        body.unit_price = Number(form.unit_price);
      }
      const { data } = await api.post(`/events-admin/${slug}/offline-ticket`, body);
      toast.success(form.mark_paid
        ? `Logged & recorded ${data.quantity} ticket${data.quantity === 1 ? '' : 's'} · $${data.amount}`
        : `Ticket order created (awaiting check) · $${data.amount}`);
      setOpen(false); reset(); onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log ticket order');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="log-offline-ticket-btn">
        <FileText className="h-3.5 w-3.5 mr-1" /> Log ticket by check
      </Button>
      <Dialog open={open} onOpenChange={o => { if (!o) { setOpen(false); reset(); } }}>
        <DialogContent className="max-w-md" data-testid="offline-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Log ticket order paid by check</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Rare — most tickets go through Stripe on the public page. Use this for guests who mailed a check instead.
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Number of tickets</Label>
                <Input type="number" min="1" max="40" value={form.quantity}
                       onChange={e => setField('quantity', e.target.value)}
                       data-testid="offline-ticket-qty" />
              </div>
              <div>
                <Label>Unit price <span className="text-gray-400 text-[10px]">(blank = current)</span></Label>
                <Input type="number" step="0.01" min="0" placeholder="Default"
                       value={form.unit_price}
                       onChange={e => setField('unit_price', e.target.value)}
                       data-testid="offline-ticket-unit-price" />
              </div>
            </div>
            <div>
              <Label>Purchaser name *</Label>
              <Input value={form.purchaser_name}
                     onChange={e => setField('purchaser_name', e.target.value)}
                     placeholder="Jane Smith" data-testid="offline-ticket-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.purchaser_email}
                     onChange={e => setField('purchaser_email', e.target.value)}
                     placeholder="jane@example.com" data-testid="offline-ticket-email" />
            </div>
            <div>
              <Label>Organization <span className="text-gray-400 font-normal text-[10px]">(optional)</span></Label>
              <Input value={form.org} onChange={e => setField('org', e.target.value)}
                     data-testid="offline-ticket-org" />
            </div>
            <div>
              <Label>Attendee names <span className="text-gray-400 font-normal text-[10px]">(optional, one per line)</span></Label>
              <textarea rows={2} value={form.attendees}
                        onChange={e => setField('attendees', e.target.value)}
                        placeholder="Jane Smith&#10;John Smith"
                        className="w-full mt-1 rounded-xl border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="offline-ticket-attendees" />
            </div>
            <div>
              <Label>Internal note <span className="text-gray-400 font-normal text-[10px]">(check #, batch, etc.)</span></Label>
              <Input value={form.note} onChange={e => setField('note', e.target.value)}
                     placeholder='e.g., Check #1054, mailed 8/12'
                     data-testid="offline-ticket-note" />
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg bg-emerald-50 border border-emerald-200"
                   data-testid="offline-ticket-mark-paid-label">
              <input type="checkbox" checked={form.mark_paid}
                     onChange={e => setField('mark_paid', e.target.checked)}
                     className="h-4 w-4 mt-0.5" data-testid="offline-ticket-mark-paid" />
              <span>
                <span className="font-medium text-emerald-900">Check already in hand — record as paid now</span>
                <span className="block text-[11px] text-emerald-700 mt-0.5">Creates the donor + gift immediately. Uncheck to log as &quot;check pending&quot; and mark paid later.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="offline-ticket-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.mark_paid ? 'Log & record' : 'Log as pending')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmbedCard({ eventName, publicUrl }) {
  const iframeCode = `<!-- The Next Step Programs — ${eventName} embed -->
<iframe id="tns-event-frame" src="${publicUrl}" width="100%" height="2400" style="border:none;border-radius:12px;display:block;" title="${eventName} — The Next Step Programs" loading="lazy"></iframe>
<script>
(function(){
  window.addEventListener('message', function(e){
    if (!e.data || e.data.source !== 'tns-event') return;
    if (e.data.type === 'tns-modal-open') {
      var f = document.getElementById('tns-event-frame');
      if (!f) return;
      var top = f.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  });
})();
<\/script>`;
  const buttonCode = `<a href="${publicUrl}" target="_blank" rel="noopener" style="display:inline-block;background:#6B3FA0;color:#fff;padding:14px 32px;border-radius:999px;font-family:sans-serif;font-weight:600;text-decoration:none;">Get tickets — ${eventName}</a>`;

  const copy = async (code, label) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Copy failed — select the code and copy manually');
    }
  };

  return (
    <Card data-testid="embed-card">
      <CardContent className="p-4 space-y-4">
        <div className="font-semibold flex items-center gap-2"><Code2 className="h-4 w-4 text-[#6B3FA0]" /> Embed on your website</div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-600">Full event page (iframe + auto-scroll) — paste into any HTML block on your site</p>
            <Button size="sm" variant="outline" onClick={() => copy(iframeCode, 'Embed code')} data-testid="copy-iframe-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
          <pre className="bg-[#2B1D3D] text-purple-100 text-[11px] rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="iframe-code">{iframeCode}</pre>
          <p className="text-[10px] text-gray-500 mt-1">
            The tiny script auto-scrolls the host page to the iframe when a buyer clicks Buy tickets or Become a sponsor, so the modal never opens off-screen.
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-600">&quot;Get tickets&quot; button — links to the event page in a new tab</p>
            <Button size="sm" variant="outline" onClick={() => copy(buttonCode, 'Button code')} data-testid="copy-button-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
          <pre className="bg-[#2B1D3D] text-purple-100 text-[11px] rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="button-code">{buttonCode}</pre>
        </div>
        <p className="text-[11px] text-gray-500">
          Tip: in WordPress use a &quot;Custom HTML&quot; block; in Squarespace an &quot;Embed/Code&quot; block; in Wix &quot;Embed HTML&quot;. The iframe height (2400px) can be adjusted to fit your layout. Purchases made inside the embed work exactly like the standalone page.
        </p>
      </CardContent>
    </Card>
  );
}

function ConfirmedSponsorsDialog({ api, slug, eventName }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/events/${slug}/sponsors`).then(r => setRows(r.data || []))
      .catch(() => toast.error('Failed to load sponsors'));
  }, [open, api, slug]);

  const setField = (idx, key, val) => setRows(r => r.map((x, i) => i === idx ? { ...x, [key]: val } : x));
  const addRow = () => setRows(r => [...r, { id: null, tier_label: '', name: '', logo_url: '', website_url: '' }]);
  const removeRow = (idx) => setRows(r => r.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    setRows(r => { const c = [...r]; [c[idx], c[j]] = [c[j], c[idx]]; return c; });
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = rows.filter(r => (r.name || '').trim() && (r.tier_label || '').trim());
      const { data } = await api.put(`/events-admin/${slug}/sponsors`, { sponsors: clean });
      toast.success(`Saved ${data.count} sponsor${data.count === 1 ? '' : 's'} — live on the event page now`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="manage-sponsors-btn">
        <Award className="h-3.5 w-3.5 mr-1" /> Manage sponsor list
      </Button>
      <Dialog open={open} onOpenChange={o => !o && setOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="manage-sponsors-dialog">
          <DialogHeader>
            <DialogTitle>Confirmed sponsors — {eventName || 'event'}</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Shown in the &quot;Thank You To Our Sponsors!&quot; section on the event page and inside the tnswork.org embed. Updates appear instantly — no re-embed needed.
            </p>
          </DialogHeader>
          <div className="space-y-2 py-2 overflow-y-auto flex-1 pr-1">
            {rows.map((row, idx) => (
              <div key={row.id || `new-${idx}`} className="p-2 border rounded-lg bg-white space-y-2" data-testid={`sponsor-row-${idx}`}>
                <div className="flex items-center gap-1.5">
                  <div className="flex flex-col shrink-0">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                            className="text-gray-400 hover:text-[#6B3FA0] disabled:opacity-30 leading-none">▲</button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1}
                            className="text-gray-400 hover:text-[#6B3FA0] disabled:opacity-30 leading-none">▼</button>
                  </div>
                  <Input value={row.name || ''} onChange={e => setField(idx, 'name', e.target.value)}
                         placeholder="Sponsor name (e.g., Medici Family)" className="flex-1"
                         data-testid={`sponsor-name-${idx}`} />
                  <button type="button" onClick={() => removeRow(idx)}
                          className="text-gray-400 hover:text-red-600 shrink-0 p-1"
                          data-testid={`sponsor-del-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 pl-6">
                  <Input value={row.tier_label || ''} onChange={e => setField(idx, 'tier_label', e.target.value)}
                         placeholder="Tier label (e.g., Silver Sponsors)" className="h-8 text-xs"
                         data-testid={`sponsor-tier-${idx}`} />
                  <Input value={row.logo_url || ''} onChange={e => setField(idx, 'logo_url', e.target.value)}
                         placeholder="Logo URL (optional)" className="h-8 text-xs"
                         data-testid={`sponsor-logo-${idx}`} />
                  <Input value={row.website_url || ''} onChange={e => setField(idx, 'website_url', e.target.value)}
                         placeholder="Website URL (optional)" className="h-8 text-xs"
                         data-testid={`sponsor-web-${idx}`} />
                </div>
              </div>
            ))}
            <button type="button" onClick={addRow}
                    className="w-full py-2 rounded-lg border border-dashed text-[#6B3FA0] hover:bg-[#F0EDF6] text-sm flex items-center justify-center gap-1"
                    data-testid="sponsor-add-row">
              <Plus className="h-4 w-4" /> Add another sponsor
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="sponsor-save-all">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

